// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import type { LanguageMetadata, LanguageTranslation } from '../types';

export const ru: LanguageTranslation = {
  translation: {
    LocaleSwitcher: {
      ru: 'Русский',
      en: 'English',
      cn: '中国人',
      be: 'Беларускі',
      label: 'Language',
    },
    SignInTelForm: {
      goToLogin: 'Назад ко входу',
      loginViaEmail: 'Вход через email',
      check2FAEmail: 'Код из почты (проверьте cпам)',
      back: 'Назад',
      onlyForRegistered: 'Только для зарегистрированных пользователей',
      confirm: 'Подтвердить',
      signIn: 'Войти',
      check2FACall: 'Код из СМС (дублируется на привязанную почту)',
      enterYourTel: 'Введите ваш телефон',
      codeSent:
        'Мы вышлем вам код. Код может быть отправлен вам на электронную почту или в виде СМС. Доставка на МТС, Билайн и Мегафон временно недоступна.',
      error:
        '{error, select, CredentialsSignin {Invalid username or password} other {Unknown error}}',
      retryCondition: 'Повторно запросить код можно через',
      minutes: 'минут',
      agreement: 'Пользуясь сервисом я соглашаюсь с',
      privacyPolicy: 'Политикой обработки персональных данных',
      and: 'и',
      terms: 'Условиями пользования сервиса',
      greetings: 'С возвращением',
      glad_to_see_you_again: 'Мы так рады видеть вас снова',
      verification_required: 'Требуется подтверждение',
      enter_2fa: 'Введите ваш одноразовый код',
      login_failed: 'Ошибка входа',
    },
    ErrorCard: {
      headerLabel: 'Упс! Что-то пошло не так!',
      backButtonLabel: 'Назад к входу',
    },
    ServerDiscovery: {
      title: 'Поиск серверов',
      serversToJoin: 'Серверы для присоединения',
      officialServerFor: 'Официальный сервер',
      members: 'Участники',
      channels: 'Каналы',
    },
    ServerPage: {
      selectChannelToStart: 'Выберите канал для начала общения',
    },
    ServerHeader: {
      invitePeople: 'Пригласить',
      serverSettings: 'Настройки сервера',
      manageMembers: 'Управление участниками',
      createChannel: 'Создать канал',
      leaveServer: 'Покинуть сервер',
      deleteServer: 'Удалить сервер',
      legal: 'Документы',
      privacy: 'Политика конфиденциальности',
      terms: 'Условия использования',
      companyInformation: 'О компании',
    },
    ServerMembersSidebar: {
      label: 'Участники',
      activity: 'Активность',
      online: 'Онлайн',
      offline: 'Не в сети',
    },
    ServerLayout: {
      company: 'КИБЕРКОАЛА',
      companyForm: 'ООО',
    },
    ServerSidebar: {
      label: 'Каналы',
      textChannels: 'Текстовые',
      voiceChannels: 'Голосовые',
      videoChannels: 'Видео',
      streamChannels: 'Трансляции',
      tribuneChannels: 'Трибуны',
      members: 'Участники',
    },
    ChannelPage: {
      watching: 'Смотрю',
    },
    NavigationServerDiscovery: {
      tooltip: 'Поиск серверов',
    },
    NavigationTalksy: {
      tooltip: 'Сообщения',
    },
    ChatMessages: {
      loading: 'Загрузка сообщений...',
      somethingWentWrong: 'Что-то пошло не так',
      loadMore: 'Загрузить ещё',
      typing: '{username} набирает...',
      copied: 'Скопировано',
      failed_to_copy: 'Ошибка копирования',
    },
    ChatInput: {
      placeholder: 'Сообщение в',
      oneTyping: 'печатает...',
      multipleTyping: 'печатают...',
      othersTyping: 'другие печатают...',
      and: 'и',
    },
    ChatItem: {
      audioNotSupported: 'Ваш браузер не поддерживает аудио.',
      edited: 'отредактировано',
      save: 'Сохранить',
      escapeToCancelEnterToSave: 'Нажмите Esc, чтобы отменить, Enter, чтобы сохранить',
      externalLinkWarning: 'Переход по внешней ссылке',
      redirectNotControlled:
        'Это внешний сайт, который не фильтруется нашей службой безопасности. Вы будете перенаправлены на',
      cancel: 'Отменить',
      continue: 'Перейти',
      read_by_you: 'Прочитано мною',
      read_by_other: 'Прочитано собеседником',
      read: 'прочитано',
      unsupported_file_type: 'Тип файла не поддерживается',
    },
    ChatHeader: {
      updates: 'Обновления',
      close: 'Close',
    },
    ChatWelcome: {
      welcome: 'Добро пожаловать в #',
      start: 'Это начало',
      channelName: 'канала.',
      conversation: 'диалога с',
    },
    Toast: {
      erorr: 'Произошла ошибка',
      somethingWentWrong: 'Что-то пошло не так',
    },
    Settings: {
      title: 'Настройки профиля',
      logout: 'Выход',
      avatar: 'Аватар',
      theme: 'Theme',
      last4Digits: 'Код из СМС',
      back: 'Назад',
      name: 'ФИО',
      phone: 'Телефон',
      currentPassword: 'Текущий пароль',
      newPassword: 'Новый пароль',
      enable2FA: 'Включить двухфакторную аутентификацию',
      requiredFields: 'Помеченные поля обязательны',
      completeYourProfile: 'Заполните профиль',
      save: 'Сохранить',
      confirm: 'Подтвердить',
      addPhone: 'Привяжите номер телефона для быстрого входа по СМС',
      removalRequest: 'Запрос удаления аккаунта',
      deleteAccount: 'Удалить аккаунт',
      email: 'Email',
      nickname: 'Псевдоним',
    },
    ModeToggle: {
      label: 'Тема',
      dark: 'Темная',
      light: 'Светлая',
      system: 'Системная',
    },
    ServerSearch: {
      search: 'Поиск',
      commandPlaceholder: 'Поиск по серверу среди участников и каналов',
      noResults: 'Ничего не нашлось',
    },
    CreateChannelModal: {
      title: 'Создать канал',
      channelName: 'Название канала',
      channelNamePlaceholder: 'Введите имя канала',
      channelType: 'Тип канала',
      channelTypePlaceholder: 'Выберите тип канала',
      create: 'Создать',
      requiredChannelName: 'Имя канала обязательно',
      private: 'Приватный',
    },
    CreateServerModal: {
      title: 'Настройка сервера',
      description:
        'Придайте серверу индивидуальность с именем и изображением. Вы всегда сможете изменить его позже. Изображение необходимо для создания сервера.',
      serverName: 'Имя сервера',
      serverNamePlaceholder: 'Введите название сервера',
      create: 'Создать',
      requiredServerName: 'Имя обязательно',
      private: 'Приватный',
    },
    DeleteChannelModal: {
      title: 'Удалить канал',
      description: 'Вы уверены, что хотите удалить канал?',
      permanentDelete: 'будет навсегда удален.',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
    },
    DeleteMessageModal: {
      title: 'Удалить сообщение',
      description: 'Вы уверены, что хотите удалить сообщение?',
      permanentDelete: 'Сообщение будет навсегда удалено.',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
    },
    DeleteServerModal: {
      title: 'Удалить сервер',
      description: 'Вы уверены, что хотите удалить cервер?',
      permanentDelete: 'будет навсегда удален.',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
    },
    EditChannelModal: {
      title: 'Редактировать канал',
      name: 'Название канала',
      placeholder: 'Введите название канала',
      type: 'Тип канала',
      placeholderType: 'Выберите тип канала',
      save: 'Сохранить',
      private: 'Приватный',
      maxParticipants: 'Лимит участников',
      maxParticipantsPlaceholder: 'Задание ограничения числа участников в канале',
      preview: 'Предварительный просмотр',
    },
    EditServerModal: {
      title: 'Редактировать сервер',
      description: 'Вы можете изменить название сервера и его описание.',
      name: 'Название сервера',
      placeholder: 'Введите название сервера',
      save: 'Сохранить',
      private: 'Приватный',
    },
    InviteModal: {
      title: 'Пригласить пользователей',
      label: 'Ссылка на приглашение',
      generate: 'Сгенерировать новую ссылку',
      generated: 'Пригласительная ссылка создана',
      error: 'Ошибка создания пригласительной ссылки',
      copyShort: 'Скопировать',
      customCode: 'Пользовательский код приглашения',
      codeHint: '6 символов с 2-3 заглавными буквами',
      useSlug: 'Использовать слаг',
      customCodeDisabled: 'Пользовательский код отключен',
      generating: 'Генерация ссылки',
      codeTaken: 'Код получен',
      copiedShort: 'Скопированный короткий код',
      copied: 'Скопировано',
      codeUppercaseError: 'Минимум 2 заглавные буквы',
      codeFormatError: 'Неверный формат кода',
    },
    LeaveFeedbackModal: {
      title: 'Оцените качество звонка',
      placeholder: 'Напишите ваш отзыв',
      cancel: 'Отменить',
      send: 'Отправить',
    },
    LeaveServerModal: {
      title: 'Покинуть сервер',
      description: 'Вы уверены, что хотите покинуть ',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
    },
    MembersModal: {
      title: 'Управление участниками',
      members: 'Участники',
      role: 'Роль',
      guest: 'Гость',
      private: 'Приватный',
      moderator: 'Модератор',
      kick: 'Удалить',
    },
    MessageFileModal: {
      title: 'Отправить файл',
      description: 'Выберите файл',
      send: 'Отправить',
    },
    ServerEvents: {
      upcoming: 'События',
      noEvents: 'Событий нет.',
      createEvent: 'Создать событие',
      where: 'Канал',
      repeatsDaily: 'Ежедневно',
      repeatsWeekly: 'Еженедельно',
      repeatsMonthly: 'Ежемесячно',
    },
    ServerEventsPage: {
      eventName: 'Название события',
      eventNamePlaceholder: 'Введите название события',
      description: 'Описание',
      descriptionPlaceholder: 'Введите описание мероприятия',
      startDate: 'Дата и время начала',
      endDate: 'Дата и время окончания',
      channel: 'Канал',
      selectChannelPlaceholder: 'Выберите канал',
      create: 'Создать',
      update: 'Изменить',
      recurrence: {
        title: 'Повторять',
        no_repeat: 'Без повтора',
        daily: 'Ежедневно',
        weekly: 'Еженедельно',
        monthly: 'Ежемесячно',
      },
      repeat_until: 'Повторять до',
    },
    UserButton: {
      onlineStatus: 'В сети',
      awayStatus: 'Отошел',
      dndStatus: 'Не беспокоить',
      invisibleStatus: 'Невидимка',
    },
    UploadFilesForm: {
      fileSizeLimit: 'Общий размер файла(ов) не должен превышать',
      upgradePlan: 'Обновите свой тариф сейчас, чтобы загрузить больше файлов',
      uploadFiles: 'Загрузить файл',
    },
    ResetPage: {
      somethingWentWrong: 'Что-то пошло не так',
      askForHelp: 'Попросить помощь',
      contact: 'Связаться',
      tryAgain: 'Попробовать снова',
    },
    AppSettings: {
      autoGainControl: 'Автоподстройка усиления микрофона',
      autoGainControlDisabledHint: 'Автоматическая регулировка усиления микрофона выключена',
      autoGainControlEnabledHint: 'Автоматическая регулировка усиления микрофона включена',
      autoconnectVoice: 'Автоподключение микрофона',
      autoconnectVoiceDisabledHint: 'Микрофон остается выключенным, пока не будет включен вручную',
      autoconnectVoiceEnabledHint:
        'Микрофон подключается автоматически при присоединении к звонку/каналу',
      captureSystemAudio: 'Захват системных звуков',
      captureSystemAudioDisabledHint: 'Не захватывать звук с устройства при показе экрана',
      captureSystemAudioEnabledHint:
        'Захватывать звук с устройства при показе экрана (Chrome, Edge, Opera)',
      conferenceVolume: 'Громкость конференции',
      devices: 'Устройства',
      disableAutoGainControl: 'Disable auto-gain control',
      disableAutoconnectVoice: 'Отключить автоподключение микрофона',
      disableEchoCancellation: 'Disable Echo Cancellation',
      disableNoiseSuppression: 'Выключить CENC',
      echoCancellation: 'Эхоподавление',
      echoCancellationDisabledHint: 'Echo Cancellation Disabled',
      echoCancellationEnabledHint: 'Echo Cancellation Enabled',
      enableAutoGainControl: 'Enable auto-gain control',
      enableAutoconnectVoice: 'Включить автоподключение микрофона',
      enableEchoCancellation: 'Enable Echo Cancellation',
      enableNoiseSuppression: 'Включить CENC',
      experimentalFeature: 'Экспериментально',
      language: 'Язык',
      noiseSuppression: 'CENC',
      noiseSuppressionDisabledHint:
        'Технология шумоподавления Calls Enhanced Noise Suppression выключена',
      noiseSuppressionEnabledHint:
        'Технология шумоподавления Calls Enhanced Noise Suppression включена',
      notifications: 'Уведомления',
      outputDevice: 'Устройства вывода',
      privacy: 'Приватность',
      sfxVolume: 'Громкость звуковых эффектов',
      simulcast: 'Разрешение',
      streamingOptions: 'StreamingOptions',
      theme: 'Тема',
      title: 'Настройки',
    },
    user_settings: {
      failed_to_update_settings: 'Ошибка обновления настроек',
      enter_verification_code: 'Пожалуйста введите ваш код из SMS',
    },
    MeSidebar: {
      find_or_start_a_conversation: 'Найти или начать беседу',
      friends: 'Друзья',
      message_requests: 'Запросы на общение',
      boost: 'Буст',
      shop: 'Магазин',
      direct_messages: 'Личные сообщения',
      outgoing_messages: 'Исходящие беседы',
      received_conversations: 'Входящие беседы',
      delete_conversation: 'Удаление беседы',
      delete_conversation_confirmation:
        'Вы уверены, что хотите удалить эту беседу? Это действие не может быть отменено.',
    },
    MeSettingsSidebar: {
      back_to_servers: 'Назад к серверам',
      search: 'Поиск...',
      nothing_found: 'Не найдено',
      user_settings: 'Настройки пользователя',
      my_account: 'Мой аккаунт',
      devices: 'Устройства',
      payment_settings: 'Оплата',
      subscriptions: 'Подписки',
      app_settings: 'Настройки приложения',
      appearance: 'Внешний вид',
      voice_n_video: 'Голос и видео',
      notifications: 'Уведомления',
      keybinds: 'Горячие клавиши',
      language: 'Язык',
      advanced: 'Продвинутые',
      games_database: 'База игр',
      legal: 'Юридическая информация',
      privacy: 'Политика конфиденциальности',
      terms: 'Условия пользования',
      company: 'О компании',
      activity_settings: 'Настройки активности',
      activity_privacy: 'Приватность',
      desktop_app_settings_title: 'Настройки приложения для ПК',
      change_password: 'Сменить пароль',
    },
    activity_privacy_page: {
      track_playing_games: 'Отслеживание игр',
      send_telemetry: 'Отправлять обезличенную телеметрию',
      receive_weekly_emails: 'Получать еженедельные письма с сообщениями из каналов (требуется активная подписка)',
      connect_steam: 'Привязать Steam',
      share_gaming_activity: 'Делиться игровой активностью в профиле',
      show_status_in_private_channels: 'Отображать статус нахождения в приватном канале',
    },
    keyboard_shortcuts: {
      title: 'Горячие клавиши',
      toggle_side_panel: 'Переключение боковой панели (альфа)',
      show_all: 'Показать все',
      toggle_theme: 'Сменить тему',
      toggle_mic: 'Переключение микрофона',
      toggle_camera: 'Переключение камеры',
      toggle_sound: 'Переключение звука (альфа)',
    },
    DeleteUserModal: {
      title: 'Удалить аккаунт',
      description: 'Учетная запись пользователя будет удалена',
      cancel: 'Отменить',
      confirm: 'Удалить',
    },
    Notifications: {
      notifications: 'Уведомления',
      description: 'Всплывающие уведомления',
      speaking_notifications: 'Уведомление при выключенном микрофоне',
      speaking_notifications_description:
        'Уведомлять, когда я говорю, но микрофон выключен или меня не слышно в канале.',
      user_start_playing: 'Уведомления о старте игры',
      user_start_playing_description: 'Уведомлять, когда участники сервера запускают игру',
    },
    Advanced: {
      streaming_options: 'Настройки трансляции',
      autorun_on_start: 'Автозапуск при старте',
      autorun_enabled: 'Включен',
      autorun_disabled: 'Выключен',
    },
    SimulcastSelector: {
      screenShareOptions: 'Выберите опции трансляции экрана (макс. 3)',
      limit: 'Вы можете выбрать до 3 вариантов.',
    },
    NavigationGetApp: {
      tooltip: 'Скачать Calls',
    },
    DevicesPage: {
      title: 'Устройства',
      name: 'Имя',
      browser: 'Браузер',
      os: 'OS',
      ip: 'IP',
      lastSeen: 'Активно',
      sessions: 'Сессии',
      location: 'Местоположение',
      fetching: 'Загрузка',
      deleting_devices_not_available: 'Отвязка устройств возможна в тарифах OPTIMUM и EXTENDED',
    },
    DragDropFileUpload: {
      dropHere: 'Перетащите сюда файлы для загрузки',
      maxFileSize: 'Максимальный размер файла:',
      uploading: 'Загрузка файлов...',
      complete: 'завершено',
      failed_to_process: 'Ошибка обработки',
      could_not_convert: 'Конвертация невозможна',
      av_not_supported: 'Кодек AV1 не поддерживается. Загрузите видео с кодеком H.264 или HEVC.',
      no_valid_files_after_processing:
        'Ошибка обработки. Загрузите видео через кнопку (+) либо используйте кодеки H.264, HEVC.',
      uploaded: 'Файл загружен',
      failed_to_upload: 'Ошибка загрузки',
    },
    message_requests: {
      accept: 'Принять ЛС',
      block: 'Заблокировать',
      report: 'Пожаловаться',
      preview_with: 'Предпросмотр беседы с',
      requests: 'Запросы общения',
      no_selection: 'Не выбрано',
    },
    video_conference_preview: {
      waiting: 'Пока никого нет. Ожидание пользователей...',
      join: 'Присоединиться',
    },
    edit_server_modal: {
      current_image: 'Текущий аватар',
    },
    user_context_menu: {
      member: 'Участник',
      on_server: 'на сервере',
      mutual_friends: 'общие друзья',
      mutual_servers: 'общие серверы',
      in_game: 'В игре',
      playing: 'играет уже',
      dm: 'Сообщение',
      role: 'Роль',
      guest: 'Гость',
      moderator: 'Модератор',
      kick: 'Кикнуть',
      friend: 'друг',
      not_friend: 'Не в друзьях',
    },
    user_context_menu_extended: {
      view_profile: 'Профиль',
      report_member: 'Пожаловаться',
      copy_user_id: 'Скопировать ID',
      invite_to_server: 'Пригласить на сервер',
      block: 'Заблокировать',
    },
    user_profile: {
      dm: 'Сообщение',
      member_since: 'Участник с',
      add_note: 'Добавить заметку (видно только вам)',
      add_note_placeholder: 'Кликните для добавления заметки',
      activity_tab: 'Активность',
      my_activity_tab: 'Моя активность',
      mutual_friends_tab: 'Общие друзья',
      mutual_servers_tab: 'Общие серверы',
      not_sharing_activity: 'не делится активностью',
      activity_placeholder:
        'Похоже пользователь практикует искусство цифрового дзена\n сделайте глубокий вдох и скажите привет',
      no_mutual_friends: 'Нет общих друзей',
      no_mutual_servers: 'Общих серверов нет',
      played_last_14_days: 'В играх',
      invite_to_server: 'Пригласить на сервер',
      no_servers_available: 'Нет доступных серверов',
      select_a_server: 'Выберите сервер',
      delete_from_friends: 'Удалить из друзей',
      delete_from_friends_confirmation:
        'Вы уверены что хотите удалить данного пользователя из друзей?',
    },
    friends_page: {
      friends: 'Друзья',
      no_friends: 'Добавьте друга на любом сервере',
      delete_button: 'Удалить',
      confirm_delete_title: 'Вы точно хотите удалить друга?',
      confirm_delete_message: 'Данное действие невозможно отменить',
      all_friends: 'Все друзья',
    },
    games_db_page: {
      games_database: 'База данных игр',
      game_not_shown: 'Игра не определяется',
      no_apps_found: 'Приложений не найдено',
      avail_in_higher_plan: 'Добавление игр доступно в тарифах OPTIMIM и EXTENDED',
    },
    desktop_page: {
      desktop_settings: 'Настройки приложения для ПК',
      run_on_startup: 'Автозагрузка',
      close_on_x: 'Закрывать при нажатии X',
      minimize_to_dock: 'Сворачивать в Панель задач',
      open_settings: 'Настройки',
      help: 'Помощь',
      check_for_updates: 'Проверка обновлений',
      reload: 'Перезапустить',
      game_tracking: 'Отслеживание запущенных игр',
      telemetry: 'Телеметрия',
      telemetry_description:
        '          Приложение собирает данные об использовании\n' +
        '          для соответствия юридическим нормам и отправляет анонимную\n' +
        '          статистику об использовании для улучшения продукта. Персональные или\n' +
        '          чувствительные данные исключены.',
      telemetry_locked: 'Настройка обязательна в рамках используемой лицензии.',
      loading_sound: 'Звук загрузки',
      reset_window_position: 'Сбросить положение на экране',
      some_settings_requires_restart:
        'Некоторые настройки применяются после перезапуска приложения',
    },
    activity_card: {
      playing: 'играет',
    },
    invite_page: {
      you_been_invited_to: 'Вы были приглашены на',
      public: 'публичный',
      private: 'приватный',
      server: 'сервер',
      join: 'Присоединиться',
      cant_join_server: 'Невозможно присоединиться к серверу',
      invite_code_missing: 'Код приглашения истек или недействителен',
    },
    participant_tile: {
      toggle_sound_on: 'Включить звук',
      toggle_sound_off: 'Выключить звук',
      unfocus: 'Убрать фокус',
      focus: 'Фокусировать',
      show_video: 'Показать видео',
      hide_video: 'Скрыть видео',
    },
    source_selector_modal: {
      share_screen_or_window: 'Поделиться экраном или окном',
      capture_sound: 'Захватывать звук',
      loading: 'Загрузка',
      start_sharing: 'Начать демонстрацию',
    },
    incoming_call_modal: {
      title: 'Входящий звонок',
      calling_you: 'звонит вам',
      decline: 'Отклонить',
      accept: 'Принять',
    },
    auth: {
      error: 'Ошибка',
      two_fa_required: 'Требуется двухфакторная аутентификация',
      check_email_for_code: 'Проверьте свою почту — код был отправлен.',
      something_went_wrong: 'Что-то пошло не так',
      welcome_back: 'С возвращением!',
      already_have_account: 'Уже есть аккаунт?',
      sign_in: 'Войти',
      forgot_password: 'Забыли пароль?',
      register: 'Зарегистрироваться',
      account_verification: 'Подтверждение аккаунта',
      back_to_login: 'Назад к входу',
      verification_failed: 'Подтверждение не удалось',
      email_verified: 'Email подтверждён! Теперь вы можете войти.',
      token_not_found: 'Токен не найден',
      send_reset_link: 'Отправить ссылку для сброса',
      password_reset_link_sent: 'Ссылка для сброса пароля отправлена на email (проверьте спам)',
      registration_failed: 'Регистрация не удалась!',
      registration_successful:
        'Регистрация успешна! Проверьте почту (и спам) для подтверждения email адреса.',
      create_an_account: 'Создать аккаунт',
      work_email: 'Электронная почта',
      phone: 'Телефон',
      password: 'Пароль',
      registering_you_agree_to_our: 'Регистрируясь, вы соглашаетесь с нашими',
      public_offer: 'Публичной офертой',
      and: 'и',
      privacy_policy: 'Политикой конфиденциальности',
      password_changed: 'Пароль изменён',
      enter_your_new_password: 'Введите новый пароль',
      new_password: 'Новый пароль',
      reset_password: 'Сбросить пароль',
      login_by_password: 'Войти по паролю',
      full_name: 'ФИО',
      full_name_placeholder: '',
      all_fields_required: 'Все поля обязательны для заполнения.',
      change_password: 'Сменить пароль',
      change_password_header: 'Задание нового пароля',
      change_password_description:
        'Вход по паролю осуществляется при наличии привязанного email адреса. Задание email производится в разделе',
      passwords_do_not_match: 'Новый пароль и повтор пароля не совпадают',
      failed_to_change_password: 'Ошибка смены пароля',
      current_password: 'Текущий пароль (пустой при отсуствии)',
      confirm_new_password: 'Повтор пароля',
      password_changed_successfully: 'Пароль успешно изменен',
      saving: 'Сохранение',
      verification_token: 'Токен',
      retry_verification: 'Повторить',
      too_many_attempts: 'Слишком много попыток. Повторите позднее',
      attempts_left: 'Осталось попыток',
      verifying: 'Проверка',
      password_min_length: 'Минимальная длина пароля - {{length}} символов',
      email_required_to_change_password: 'Задайте ваш email перед тем как менять пароль',
      email_verification_required_to_change_password:
        'Перед сменой пароля подтвердите свой адрес электронной почты.',
      loading_user_data: 'Загрузка информации профиля…',
      email_verified_on: '✓ Email подтвержден {{date}}',
    },
    games_library: {
      title: 'Библиотека игр',
      library: 'Библиотека',
      game_count: '{{count}} шт.',

      all_games: 'Игры',
      recently_played: 'Недавние',
      mutual_games: 'Общие игры',

      hours_played: 'часов сыграно',
      hours_short: 'ч. сыграно',
      minutes_short: 'мин. сыграно',

      play: 'Играть',
      details: 'Подробнее',

      search_placeholder: 'Поиск игр...',

      no_games: 'Игры не найдены',
    },
    routes: {
      friends: 'Друзья',
      messages: 'Сообщения',
      discovery: 'Поиск серверов',
      voice_video_settings: 'Настройки аудио и видео',
      account: 'Аккаунт',
      message_requests: 'Запросы на общение',
      desktop_app: 'Настройки приложения для ПК',
      devices: 'Устройства',
      subscriptions: 'Подписка',
      appearance: 'Внешний вид',
      notifications: 'Уведомления',
      keybinds: 'Горячие клавиши',
      language: 'Язык',
      advanced: 'Продвинутые',
      gamesdb: 'База игр',
      games_library: 'Библиотека игр',
      activity_privacy: 'Приватность',
      privacy_policy: 'Политика конфиденциальности',
      terms_of_use: 'Условия пользования сервисом',
      change_password: 'Сменить пароль',
    },
    time: {
      minutes: 'мин',
      hours: 'ч',
      days: 'д',
      now: 'сейчас',
      ago: 'назад',
      weeks: 'недели',
    },
    common: {
      cancel: 'Отменить',
      delete: 'Удалить',
      minutes: 'мин.',
      loading: 'Загрузка',
      go_back_to_dms: 'Назад к ЛС',
      drop_here: 'Перетащите файл сюда',
      max_file_size: 'Максимальный размер:',
      processing_video: 'Обработка видео...',
      uploading: 'Загрузка',
      complete: 'Завершено',
      something_went_wrong: 'Что-то пошло не так. Повторите позднее.',
      success: 'Успешно',
      go_to_server: 'Перейти к серверу',
      error: 'Ошибка',
      upgrade_your_account: 'Улучшите свой аккаунт',
      month: 'мес',
      year: 'год',
      need_help_with_order: 'Нужна помощь с заказом',
      no_products_found: 'Доступных услуг не найдено',
      back: 'Назад',
      available_in_subscription: 'Доступно по подписке',
      session_lifetime: 'Сохранение сессии учетной записи',
      action_na: 'Действие недоступно',
      go_to_subscriptions: 'К подпискам',
      search: 'Поиск',
      online: 'Онлайн',
      offline: 'Не в сети',
      all: 'Все',
      screen: 'демонстрирует',
      more_in_desktop_app: 'Больше в приложении для ПК',
      starting: 'начинаем',
      close: 'Закрыть',
      invite: 'Добавить',
    },
    // ChartsDB
    menu: {
      file: {
        file: 'Файл',
        new: 'Создать',
        open: 'Открыть',
        save: 'Сохранить',
        import: 'Импортировать базу данных',
        export_sql: 'Экспорт SQL',
        export_as: 'Экспортировать как',
        delete_diagram: 'Удалить диаграмму',
        exit: 'Выход',
      },
      edit: {
        edit: 'Изменение',
        undo: 'Отменить',
        redo: 'Вернуть',
        clear: 'Очистить',
      },
      view: {
        view: 'Вид',
        show_sidebar: 'Показать боковую панель',
        hide_sidebar: 'Скрыть боковую панель',
        hide_cardinality: 'Скрыть множественность связи',
        show_cardinality: 'Показать множественность связи',
        zoom_on_scroll: 'Увеличение при прокрутке',
        theme: 'Тема',
        show_dependencies: 'Показать зависимости',
        hide_dependencies: 'Скрыть зависимости',
        show_minimap: 'Show Mini Map',
        hide_minimap: 'Hide Mini Map',
      },
      backup: {
        backup: 'Backup',
        export_diagram: 'Export Diagram',
        restore_diagram: 'Restore Diagram',
      },
      help: {
        help: 'Помощь',
        docs_website: 'Документация',
        join_discord: 'Присоединиться к сообществу в Discord',
      },
    },

    delete_diagram_alert: {
      title: 'Удалить диаграмму',
      description: 'Это действие нельзя отменить. Это навсегда удалит диаграмму.',
      cancel: 'Отменить',
      delete: 'Удалить',
    },

    clear_diagram_alert: {
      title: 'Очистить диаграмму',
      description: 'Это действие нельзя отменить. Это навсегда удалит все данные в диаграмме.',
      cancel: 'Отменить',
      clear: 'Очистить',
    },

    reorder_diagram_alert: {
      title: 'Переупорядочить диаграмму',
      description: 'Это действие переставит все таблицы на диаграмме. Хотите продолжить?',
      reorder: 'Изменить порядок',
      cancel: 'Отменить',
    },

    multiple_schemas_alert: {
      title: 'Множественные схемы',
      description:
        '{{schemasCount}} схем в этой диаграмме. В данный момент отображается: {{formattedSchemas}}.',
      dont_show_again: 'Больше не показывать',
      change_schema: 'Изменить',
      none: 'никто',
    },

    copy_to_clipboard_toast: {
      unsupported: {
        title: 'Ошибка копирования',
        description: 'Буфер обмена не поддерживается',
      },
      failed: {
        title: 'Ошибка копирования',
        description: 'Что-то пошло не так. Пожалуйста, попробуйте еще раз.',
      },
    },

    theme: {
      system: 'Системная',
      light: 'Светлая',
      dark: 'Темная',
    },

    zoom: {
      on: 'Включено',
      off: 'Выключено',
    },

    last_saved: 'Последнее сохранение',
    saved: 'Сохранено',
    loading_diagram: 'Загрузка диаграммы...',
    deselect_all: 'Отменить выбор всех',
    select_all: 'Выбрать все',
    clear: 'Очистить',
    show_more: 'Показать больше',
    show_less: 'Показать меньше',

    side_panel: {
      schema: 'Схема:',
      filter_by_schema: 'Фильтр по схеме',
      search_schema: 'Схема поиска...',
      no_schemas_found: 'Схемы не найдены.',
      view_all_options: 'Просмотреть все варианты...',
      tables_section: {
        tables: 'Таблицы',
        add_table: 'Добавить таблицу',
        filter: 'Фильтр',
        collapse: 'Свернуть все',
        clear: 'Clear Filter',
        no_results: 'No tables found matching your filter.',
        show_list: 'Show Table List',
        show_dbml: 'Show DBML Editor',

        table: {
          fields: 'Поля',
          nullable: 'Может содержать NULL?',
          primary_key: 'Первичный ключ,',
          indexes: 'Индексы',
          comments: 'Комментарии',
          no_comments: 'Нет комментария',
          add_field: 'Добавить поле',
          add_index: 'Добавить индекс',
          index_select_fields: 'Выберите поля',
          no_types_found: 'Типы не найдены',
          field_name: 'Имя',
          field_type: 'Тип',
          field_actions: {
            title: 'Атрибуты поля',
            unique: 'Уникальный',
            comments: 'Комментарии',
            no_comments: 'Нет комментария',
            delete_field: 'Удалить поле',
            character_length: 'Max Length',
          },
          index_actions: {
            title: 'Атрибуты индекса',
            name: 'Имя',
            unique: 'Уникальный',
            delete_index: 'Удалить индекс',
          },
          table_actions: {
            title: 'Действия',
            change_schema: 'Изменить схему',
            add_field: 'Добавить поле',
            add_index: 'Добавить индекс',
            duplicate_table: 'Duplicate Table',
            delete_table: 'Удалить таблицу',
          },
        },
        empty_state: {
          title: 'Нет таблиц',
          description: 'Создайте таблицу, чтобы начать',
        },
      },
      relationships_section: {
        relationships: 'Отношения',
        filter: 'Фильтр',
        add_relationship: 'Добавить отношение',
        collapse: 'Свернуть все',
        relationship: {
          primary: 'Основная таблица',
          foreign: 'Справочная таблица',
          cardinality: 'Тип множественности связи',
          delete_relationship: 'Удалить',
          relationship_actions: {
            title: 'Действия',
            delete_relationship: 'Удалить',
          },
        },
        empty_state: {
          title: 'Нет отношений',
          description: 'Создайте связь для соединения таблиц',
        },
      },
      dependencies_section: {
        dependencies: 'Зависимости',
        filter: 'Фильтр',
        collapse: 'Свернуть все',
        dependency: {
          table: 'Стол',
          dependent_table: 'Зависимый вид',
          delete_dependency: 'Удалить',
          dependency_actions: {
            title: 'Действия',
            delete_dependency: 'Удалить',
          },
        },
        empty_state: {
          title: 'Нет зависимостей',
          description: 'Создайте представление, чтобы начать',
        },
      },
    },

    toolbar: {
      zoom_in: 'Увеличить масштаб',
      zoom_out: 'Уменьшить масштаб',
      save: 'Сохранить',
      show_all: 'Показать все',
      undo: 'Отменить',
      redo: 'Вернуть',
      reorder_diagram: 'Переупорядочить диаграмму',
      highlight_overlapping_tables: 'Выделение перекрывающихся таблиц',
    },

    new_diagram_dialog: {
      database_selection: {
        title: 'Какова ваша база данных?',
        description: 'Каждая база данных имеет свои уникальные функции и возможности.',
        check_examples_long: 'Открыть примеры',
        check_examples_short: 'Примеры',
      },

      import_database: {
        title: 'Импортируйте свою базу данных',
        database_edition: 'Версия базы данных:',
        step_1: 'Запустите этот скрипт в своей базе данных:',
        step_2: 'Вставьте вывод скрипта сюда:',
        script_results_placeholder: 'Вывод скрипта здесь...',
        ssms_instructions: {
          button_text: 'SSMS Инструкции',
          title: 'Инструкции',
          step_1:
            'Откройте в меню пункты Инструменты > Параметры > Результаты запроса > SQL Сервер.',
          step_2:
            'Если вы используете "Результат в сетке," измените Максимальное количество извлекаемых символов для данных, отличных от XML (установите на 9999999).',
        },
        instructions_link: 'Нужна помощь? Посмотрите, как',
        check_script_result: 'Проверить результат выполнения скрипта',
      },

      cancel: 'Отменить',
      back: 'Назад',
      import_from_file: 'Импортировать из файла',
      empty_diagram: 'Пустая диаграмма',
      continue: 'Продолжить',
      import: 'Импорт',
    },

    open_diagram_dialog: {
      title: 'Открыть диаграмму',
      description: 'Выберите диаграмму, которую нужно открыть, из списка ниже.',
      table_columns: {
        name: 'Имя',
        created_at: 'Создано в',
        last_modified: 'Последнее изменение',
        tables_count: 'Таблицы',
      },
      cancel: 'Отмена',
      open: 'Открыть',
    },

    export_sql_dialog: {
      title: 'Экспорт SQL',
      description: 'Экспортируйте схему диаграммы в {{databaseType}} скрипт',
      close: 'Закрыть',
      loading: {
        text: 'ИИ генерирует SQL для {{databaseType}}...',
        description: 'Это должно занять до 30 секунд.',
      },
      error: {
        message:
          'Ошибка создания скрипта SQL. Попробуйте еще раз позже или <0>свяжитесь с нами</0>.',
        description: 'Не стесняйтесь использовать ваш OPENAI_TOKEN, см. руководство <0>здесь</0>.',
      },
    },

    create_relationship_dialog: {
      title: 'Создать отношениe',
      primary_table: 'Основная таблица',
      primary_field: 'Основное поле',
      referenced_table: 'Ссылается на таблицу',
      referenced_field: 'Ссылается на поле',
      primary_table_placeholder: 'Выберите таблицу',
      primary_field_placeholder: 'Выберите поле',
      referenced_table_placeholder: 'Выберите таблицу',
      referenced_field_placeholder: 'Выберите поле',
      no_tables_found: 'Таблицы не найдены',
      no_fields_found: 'Поля не найдены',
      create: 'Создать',
      cancel: 'Отменить',
    },

    import_database_dialog: {
      title: 'Импорт в текущую диаграмму',
      override_alert: {
        title: 'Импортировать базу данных',
        content: {
          alert: 'Импорт этой диаграммы повлияет на существующие таблицы и связи.',
          new_tables: '<bold>{{newTablesNumber}}</bold> будут добавлены новые таблицы.',
          new_relationships:
            '<bold>{{newRelationshipsNumber}}</bold> будут созданы новые отношения.',
          tables_override: '<bold>{{tablesOverrideNumber}}</bold> таблицы будут перезаписаны.',
          proceed: 'Хотите продолжить?',
        },
        import: 'Импорт',
        cancel: 'Отмена',
      },
    },

    export_image_dialog: {
      title: 'Экспортировать изображение',
      description: 'Выберите детализацию изображения при экспорте:',
      scale_1x: '1x Обычный',
      scale_2x: '2x (Рекомендовано)',
      scale_3x: '3x',
      scale_4x: '4x',
      cancel: 'Отменить',
      export: 'Экспортировать',
    },

    new_table_schema_dialog: {
      title: 'Выбрать схему',
      description:
        'В настоящее время отображается несколько схем. Выберите одну для новой таблицы.',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
    },

    update_table_schema_dialog: {
      title: 'Изменить схему',
      description: 'Обновить таблицу "{{tableName}}" схема',
      cancel: 'Отменить',
      confirm: 'Изменить',
    },

    star_us_dialog: {
      title: 'Помогите нам стать лучше!',
      description: 'Хотите отметить нас на GitHub? Это всего лишь один клик!',
      close: 'Не сейчас',
      confirm: 'Конечно!',
    },
    export_diagram_dialog: {
      title: 'Экспорт кода диаграммы',
      description: 'Выберите формат экспорта:',
      format_json: 'JSON',
      cancel: 'Отменить',
      export: 'Экспортировать',
      error: {
        title: 'Ошибка экспортирования диаграммы',
        description:
          'Что-то пошло не так. Если вам нужна помощь, напишите нам: calls@cyberkoala.ru',
      },
    },
    import_diagram_dialog: {
      title: 'Импорт кода диаграммы',
      description: 'Вставьте JSON код диаграммы ниже:',
      cancel: 'Отменить',
      import: 'Импортировать',
      error: {
        title: 'Ошибка при импорте диаграммы',
        description:
          'Код JSON диаграммы некорректен. Проверьте, пожалуйста, код и попробуйте снова. Проблема не решается? Напишите нам: calls@cyberkoala.ru',
      },
    },
    import_dbml_dialog: {
      example_title: 'Import Example DBML',
      title: 'Import DBML',
      description: 'Import a database schema from DBML format.',
      import: 'Import',
      cancel: 'Cancel',
      skip_and_empty: 'Skip & Empty',
      show_example: 'Show Example',
      error: {
        title: 'Error',
        description: 'Failed to parse DBML. Please check the syntax.',
      },
    },
    relationship_type: {
      one_to_one: 'Один к одному',
      one_to_many: 'Один ко многим',
      many_to_one: 'Многие к одному',
      many_to_many: 'Многие ко многим',
    },

    canvas_context_menu: {
      new_table: 'Создать таблицу',
      new_relationship: 'Создать отношение',
    },

    table_node_context_menu: {
      edit_table: 'Изменить таблицу',
      duplicate_table: 'Duplicate Table',
      delete_table: 'Удалить таблицу',
      add_relationship: 'Add Relationship',
    },

    copy_to_clipboard: 'Скопировать в буфер обмена',
    copied: 'Скопировано!',
    snap_to_grid_tooltip: 'Выравнивание по сетке (Удерживайте {{key}})',
    tool_tips: {
      double_click_to_edit: 'Кликните дважды, чтобы изменить',
    },

    language_select: {
      change_language: 'Сменить язык',
    },

    importantUpdateRequired: {
      title: 'Требуется обновление десктопного приложения',
      description: 'Поддержка сертификатов старой версии прекращена. Скачайте новую версию с ccalls.site',
      download: 'Скачать',
      close: 'Закрыть',
    },
  },
};

export const ruMetadata: LanguageMetadata = {
  name: 'Russian',
  nativeName: 'Русский',
  code: 'ru',
};
